import { formatBytes, formatDate, type Passport } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText } from "lucide-react";
import { useState } from "react";

import { Badge, Spinner } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

const STATUS_KEYS = {
  pending: "pass.status_pending",
  processing: "pass.status_processing",
  ready: "pass.status_ready",
  failed: "pass.status_failed",
} as const;
const TONES = { pending: "info", processing: "info", ready: "success", failed: "danger" } as const;
const BUSY = ["pending", "processing"];

/** "Uy elektr pasporti" PDF: tayyorlash (fonda), holatni kuzatish va yuklab olish. Mijoz ham, xodim ham ishlatadi. */
export function PassportPanel({ propertyId }: { propertyId: string }) {
  const { t, lang: uiLang } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [lang, setLang] = useState<"uz" | "ru">(uiLang);
  const key = ["passports", propertyId];

  const list = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/{id}/passport/", { params: { path: { id: propertyId } } }),
      ),
    // tayyor bo'lguncha vaqti-vaqti bilan so'raymiz
    refetchInterval: (q) => (q.state.data?.some((e) => BUSY.includes(e.status)) ? 2000 : false),
  });
  const exports: Passport[] = list.data ?? [];
  const busy = exports.some((e) => BUSY.includes(e.status));

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.POST("/api/properties/{id}/passport/", {
        params: { path: { id: propertyId } },
        body: { lang },
      });
      return unwrap(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label={t("pass.lang")}
          className="w-36"
          value={lang}
          onChange={(e) => setLang(e.target.value as "uz" | "ru")}
        >
          <option value="uz">O‘zbekcha</option>
          <option value="ru">Русский</option>
        </Select>
        <Button onClick={() => create.mutate()} disabled={create.isPending || busy}>
          <FileText className="size-4" /> {busy ? t("pass.preparing") : t("pass.create")}
        </Button>
        {busy && <Spinner />}
      </div>
      <p className="text-xs text-muted-foreground">{t("pass.hint")}</p>

      {list.isLoading && <Spinner />}
      {exports.length > 0 && (
        <ul
          className="divide-y divide-border rounded-lg border border-border"
          aria-label={t("pass.history")}
        >
          {exports.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span>{formatDate(e.created_at)}</span>
                <span className="uppercase text-muted-foreground">{e.lang}</span>
                <Badge tone={TONES[e.status]}>{t(STATUS_KEYS[e.status])}</Badge>
                {e.status === "ready" && (
                  <span className="text-xs text-muted-foreground">
                    {t("pass.pages", { n: e.pages })} · {formatBytes(e.file_size)}
                  </span>
                )}
                {e.status === "failed" && <span className="text-xs text-danger">{e.error}</span>}
              </div>
              {e.status === "ready" && e.download_url && (
                <Button asChild size="sm" variant="outline">
                  <a href={e.download_url} download>
                    <Download className="size-4" /> PDF
                  </a>
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
